import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, absUrl, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CardContent from "@/components/CardContent";
import { toast } from "sonner";
import { ArrowLeft, Save, UploadCloud, X, Loader2, Building2, Phone, Share2, Palette, ImageIcon } from "lucide-react";

const EMPTY = {
  company_name: "", full_name: "", profession: "", phone: "", whatsapp: "", email: "",
  website: "", address: "", instagram: "", facebook: "", linkedin: "", tiktok: "",
  description: "", logo_path: "", logo_size: "medium", logo_shape: "original",
  template: "nero", custom_color: "#4F46E5", secondary_color: "#0EA5E9", accent_color: "#F59E0B", status: "Bozza",
};

const TEMPLATES = [
  { id: "nero", name: "Nero elegante", testId: "select-template-nero", swatch: "bg-zinc-900" },
  { id: "bianco", name: "Bianco minimal", testId: "select-template-bianco", swatch: "bg-white border border-slate-300" },
  { id: "custom", name: "Colore personalizzato", testId: "select-template-custom", swatch: "bg-indigo-500" },
];

export default function CardEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    api.get(`/cards/${id}`).then(({ data }) => {
      const { logo_url, public_url, created_at, updated_at, ...rest } = data;
      setForm({ ...EMPTY, ...rest });
      setSavedCard(data);
      setLogoUrl(absUrl(logo_url));
    }).catch((e) => { toast.error(apiErr(e)); navigate("/"); });
  }, [id, isNew, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Seleziona un'immagine (PNG, JPG, WEBP, SVG)");
    setLogoUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, logo_path: data.path }));
      setLogoUrl(absUrl(data.url));
      toast.success("Logo caricato");
    } catch (e) {
      toast.error(apiErr(e));
      setLogoUrl("");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    uploadFile(file);
  };

  const removeLogo = () => {
    setForm((f) => ({ ...f, logo_path: "" }));
    setLogoUrl("");
  };

  const save = async () => {
    if (!form.company_name.trim() && !form.full_name.trim()) {
      return toast.error("Inserisci almeno il nome azienda o della persona");
    }
    setSaving(true);
    try {
      let card;
      if (isNew) {
        const { data } = await api.post("/cards", form);
        card = data;
        toast.success("Carta creata ✅");
        navigate(`/card/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/cards/${id}`, form);
        card = data;
        toast.success("Modifiche salvate — la pagina online è aggiornata");
      }
      setSavedCard(card);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const previewCard = form;
  const qrUrl = savedCard ? `${API}/public/cards/${savedCard.id}/qr.png` : "";
  const vcardUrl = savedCard ? `${API}/public/cards/${savedCard.id}/vcard` : "";

  return (
    <div className="min-h-screen bg-slate-50"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}>
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1500px] px-4 sm:px-8 py-3.5 flex items-center gap-3">
          <Button data-testid="btn-back" variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="font-display font-bold text-slate-900">{isNew ? "Nuova carta NFC" : "Modifica carta"}</h1>
            {savedCard && <p className="text-[11px] font-mono text-indigo-500">{savedCard.public_url}</p>}
          </div>
          <Button data-testid="btn-submit-card-form" onClick={save} disabled={saving}
            className="ml-auto h-12 px-6 font-semibold bg-indigo-600 hover:bg-indigo-700 rounded-2xl">
            {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
            {isNew ? "Crea carta" : "Salva"}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-4 sm:px-8 py-8 lg:grid lg:grid-cols-12 lg:gap-8 items-start">
        {/* Form */}
        <div className="lg:col-span-7 space-y-5">
          <Section icon={Building2} title="Dati aziendali e personali">
            <Field label="Nome azienda"><Input data-testid="input-company-name" value={form.company_name} onChange={set("company_name")} placeholder="Es. Rossi Design SA" /></Field>
            <Field label="Nome e cognome (facoltativo)"><Input data-testid="input-full-name" value={form.full_name} onChange={set("full_name")} placeholder="Mario Rossi" /></Field>
            <Field label="Professione / attività"><Input data-testid="input-profession" value={form.profession} onChange={set("profession")} placeholder="Architetto" /></Field>
            <Field label="Descrizione breve"><Textarea data-testid="textarea-description" value={form.description} onChange={set("description")} rows={3} placeholder="Una frase che descrive l'attività..." /></Field>
          </Section>

          <Section icon={ImageIcon} title="Logo">
            <div data-testid="dropzone-logo-upload" onClick={() => fileRef.current?.click()}
              className={`relative flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
                dragging ? "border-indigo-500 bg-indigo-50" : "border-slate-300 hover:border-indigo-400 hover:bg-slate-50"
              }`}>
              {logoUrl ? (
                <div className="relative">
                  <img src={logoUrl} alt="logo" className="h-28 w-28 rounded-2xl object-cover ring-1 ring-slate-200" />
                  {uploading && <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40"><Loader2 className="animate-spin text-white" size={22} /></div>}
                  <button data-testid="btn-remove-logo" onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                    className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow"><X size={15} /></button>
                </div>
              ) : (
                <>
                  <UploadCloud className="text-slate-400" size={34} />
                  <p className="text-sm font-medium text-slate-600">Trascina qui il logo o clicca per selezionare</p>
                  <p className="text-xs text-slate-400">PNG, JPG, WEBP, SVG · max 5MB</p>
                </>
              )}
              <input ref={fileRef} data-testid="input-logo-file" type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-sm font-medium text-slate-600 mb-2 block">Dimensione logo</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[["small", "Piccolo"], ["medium", "Medio"], ["large", "Grande"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`select-logo-size-${v}`} onClick={() => setForm((f) => ({ ...f, logo_size: v }))}
                      className={`rounded-xl border-2 py-2 text-xs font-semibold transition-all ${form.logo_size === v ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-600 mb-2 block">Formato logo</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[["original", "Originale"], ["square", "Quadrato"], ["rectangle", "Rettang."]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`select-logo-shape-${v}`} onClick={() => setForm((f) => ({ ...f, logo_shape: v }))}
                      className={`rounded-xl border-2 py-2 text-[11px] font-semibold transition-all ${form.logo_shape === v ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          <Section icon={Phone} title="Contatti diretti">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Telefono"><Input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+41 79 123 45 67" /></Field>
              <Field label="WhatsApp"><Input data-testid="input-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+41 79 123 45 67" /></Field>
              <Field label="Email"><Input data-testid="input-email" value={form.email} onChange={set("email")} placeholder="info@azienda.ch" /></Field>
              <Field label="Sito web"><Input data-testid="input-website" value={form.website} onChange={set("website")} placeholder="www.azienda.ch" /></Field>
            </div>
            <Field label="Indirizzo"><Input data-testid="input-address" value={form.address} onChange={set("address")} placeholder="Via Roma 1, 6900 Lugano" /></Field>
          </Section>

          <Section icon={Share2} title="Profili social">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Instagram"><Input data-testid="input-instagram" value={form.instagram} onChange={set("instagram")} placeholder="@utente" /></Field>
              <Field label="Facebook"><Input data-testid="input-facebook" value={form.facebook} onChange={set("facebook")} placeholder="pagina" /></Field>
              <Field label="LinkedIn"><Input data-testid="input-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="profilo" /></Field>
              <Field label="TikTok (facoltativo)"><Input data-testid="input-tiktok" value={form.tiktok} onChange={set("tiktok")} placeholder="@utente" /></Field>
            </div>
          </Section>

          <Section icon={Palette} title="Template e stato">
            <Label className="text-sm font-medium text-slate-600 mb-2 block">Template</Label>
            <div className="grid grid-cols-3 gap-3">
              {TEMPLATES.map((tp) => (
                <button key={tp.id} data-testid={tp.testId} onClick={() => setForm((f) => ({ ...f, template: tp.id }))}
                  className={`rounded-2xl border-2 p-3 text-left transition-all ${form.template === tp.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className={`mb-2 h-10 w-full rounded-lg ${tp.swatch}`} />
                  <p className="text-xs font-semibold text-slate-700">{tp.name}</p>
                </button>
              ))}
            </div>
            {(form.template === "nero" || form.template === "bianco") && (
              <div className="mt-4">
                <Label className="text-sm font-medium text-slate-600 mb-2 block">Colore accento</Label>
                <div className="flex items-center gap-3">
                  <input data-testid="input-accent-color" type="color" value={form.accent_color} onChange={set("accent_color")}
                    className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                  <span className="font-mono text-sm text-slate-500">{form.accent_color}</span>
                </div>
                <p className="mt-1 text-xs text-slate-400">Personalizza pulsanti, icone ed evidenziazioni. Lo sfondo resta {form.template === "nero" ? "scuro" : "bianco"}.</p>
              </div>
            )}
            {form.template === "custom" && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-600 mb-2 block">Colore principale</Label>
                  <div className="flex items-center gap-3">
                    <input data-testid="input-custom-color" type="color" value={form.custom_color} onChange={set("custom_color")}
                      className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                    <span className="font-mono text-sm text-slate-500">{form.custom_color}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600 mb-2 block">Colore secondario / accento</Label>
                  <div className="flex items-center gap-3">
                    <input data-testid="input-secondary-color" type="color" value={form.secondary_color} onChange={set("secondary_color")}
                      className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                    <span className="font-mono text-sm text-slate-500">{form.secondary_color}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-5">
              <Label className="text-sm font-medium text-slate-600 mb-2 block">Stato carta</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="select-status" className="w-full sm:w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Bozza", "Pronta", "NFC scritto", "Spedita"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Section>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-5 mt-8 lg:mt-0 lg:sticky lg:top-24 flex flex-col items-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Anteprima live</p>
          <div className="relative w-[340px] h-[700px] rounded-[48px] bg-slate-950 p-3 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] border-4 border-slate-800">
            <div className="absolute left-1/2 top-3 z-10 h-6 w-32 -translate-x-1/2 rounded-full bg-slate-950" />
            <div className="h-full w-full overflow-y-auto scrollbar-thin rounded-[36px]">
              <CardContent data={previewCard} logoUrl={logoUrl} qrUrl={qrUrl} vcardUrl={vcardUrl} interactive={false} />
            </div>
          </div>
          {!savedCard && <p className="mt-3 text-xs text-slate-400 text-center max-w-xs">QR e vCard saranno disponibili dopo aver creato la carta.</p>}
        </div>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><Icon size={17} /></div>
        <h2 className="font-display font-bold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
